import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { useHistory, useLocation } from 'react-router-dom';
import { getQueryString } from 'utils/address.util';
import { loadRestaurants, setSelectedTags } from 'redux/restaurants/actions';
import { getFilteredChefs } from 'selectors/restaurants';
import {
  isRestaurantOpenNow,
  getOrderNoticeLabel,
} from 'utils/restaurants.util';
import { authenticateWithToken } from 'api/authApi';
import { signinRequest } from 'redux/actions/authActions';
import DeliveryInfoFormInline from 'components/common/DeliveryInfoForm/DeliveryInfoFormInline';
import { BrowseView } from 'panda-ui/components/BrowseView';
import './index.scss';

const queryString = require('query-string');

const MEAL_IMAGES = [
  '/assets/img/samples/meal_1.jpg',
  '/assets/img/samples/meal_2.jpg',
  '/assets/img/samples/meal_3.jpg',
  '/assets/img/samples/meal_4.jpg',
  '/assets/img/samples/meal_5.jpg',
  '/assets/img/samples/meal_6.jpg',
  '/assets/img/samples/meal_7.jpg',
  '/assets/img/samples/meal_8.jpg',
];

const pickMealImage = key => {
  const seed = String(key || '')
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return MEAL_IMAGES[seed % MEAL_IMAGES.length];
};

const mapChefToRestaurant = chef => {
  const id = get(chef, 'id') || get(chef, 'slug');
  const slug = get(chef, 'slug') || id;
  const name = get(chef, 'name') || 'Restaurant';
  const chefIds = get(chef, 'chefIds') || [];
  const chefImages = get(chef, 'chefImages') || {};
  const profileImageUrl = get(chefImages, [chefIds[0], 'profileImageUrl']);
  const searchResultImageUrl = get(chef, 'searchResultImageUrl');
  const avatarImage = profileImageUrl || searchResultImageUrl || '/panda_avatar.jpg';
  const fallbackImage = pickMealImage(id || name);
  const coverImage =
    searchResultImageUrl || get(chef, 'bannerImageUrl') || fallbackImage;
  const tagline =
    get(chef, 'description') ||
    get(chef, 'shortAddress') ||
    'Delicious food delivered to you';
  const cuisineTypes = get(chef, 'cuisineTypes') || [];
  const presetTags = get(chef, 'presetTags') || [];
  const categories = [...new Set([...cuisineTypes, ...presetTags])];
  const distance = parseFloat(get(chef, 'distance')) || 0.1;
  const distanceUnit = get(chef, 'distanceUnit') || 'mi';
  const distanceMiles =
    distanceUnit.toLowerCase() === 'km' ? distance * 0.621371 : distance;
  const rating = parseFloat(get(chef, 'averageRating')) || 0;
  const orderedCount = parseInt(get(chef, 'orderCount')) || 0;
  const noticeHours = parseInt(get(chef, 'preOrderNoticeHour')) || 0;
  const minimumCents = parseInt(get(chef, 'minimumOrderAmountCents')) || 0;
  const deliveryFeeCents = parseInt(get(chef, 'deliveryPriceCents')) || 0;
  const storeHours = get(chef, 'storeHours');
  const availableForOrdering = get(chef, 'availableForOrdering') !== false;

  return {
    id,
    slug,
    name,
    tagline,
    coverImage,
    fallbackImage,
    avatarImage,
    rating,
    reviewCount: orderedCount,
    orderedCount,
    cuisine: cuisineTypes[0] || '',
    category: cuisineTypes[0] || presetTags[0] || 'all',
    distanceMiles: Number(distanceMiles.toFixed(1)),
    deliveryFee: deliveryFeeCents / 100,
    deliveryTime: getOrderNoticeLabel(noticeHours),
    noticeHours,
    openNow: isRestaurantOpenNow(storeHours),
    minimumOrder: minimumCents / 100,
    address: get(chef, 'shortAddress') || '',
    isNew: false,
    categories,
    menu: [],
    availableForOrdering,
  };
};

const PandaBrowse = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const userSignedIn = useSelector(state => get(state, 'user.user.confirmed'));
  const {
    loading,
    loadingMore,
    currentPage,
    totalPage,
    filterTags,
    selectedFilterTags,
    filterTagsIcon,
  } = useSelector(state => state.restaurants);
  const [filteredChefs = []] = useSelector(getFilteredChefs);
  const [formDataQuery, setFormDataQuery] = useState(getQueryString());
  const [searchQuery] = useState('');
  const [loadingRestaurant, setLoadingRestaurant] = useState(null);
  const debounceTimer = useRef(null);
  const { token } = queryString.parse(search);

  async function fetchUserData() {
    try {
      const auth = await authenticateWithToken({
        Authorization: `Bearer ${token}`,
      });
      if (auth && auth.user && auth.user.confirmed) {
        dispatch(signinRequest(auth));
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
    } catch (e) {
      console.log('error', e);
    }
  }

  useEffect(() => {
    if (token) {
      fetchUserData();
    }
  }, [token]);

  const onDeliveryFormSubmit = query => {
    setFormDataQuery(query);
  };

  const fetchData = ({ page }) => {
    dispatch(
      loadRestaurants({
        queryParams: formDataQuery,
        page: page || currentPage,
        loadingMore: false,
      }),
    );
  };

  useEffect(() => {
    fetchData({ page: 1 });
  }, [formDataQuery, userSignedIn]);

  const handleSelect = tags => {
    dispatch(setSelectedTags(tags));
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      dispatch(
        loadRestaurants({
          queryParams: getQueryString({
            preset_category_names: tags,
          }),
        }),
      );
    }, 1000);
  };

  const handleCancel = () => {
    dispatch(loadRestaurants({ queryParams: formDataQuery, page: 1 }));
    dispatch(setSelectedTags([]));
  };

  const onLoadMore = () => {
    if (!loadingMore && totalPage > currentPage) {
      dispatch(
        loadRestaurants({
          queryParams: getQueryString({
            preset_category_names: selectedFilterTags,
          }),
          page: currentPage + 1,
          loadingMore: true,
        }),
      );
    }
  };

  const mappedRestaurants = (filteredChefs || []).map(mapChefToRestaurant);

  const categories = (filterTags || []).map(tag => ({
    id: tag,
    name: tag,
    icon:
      typeof get(filterTagsIcon, tag) === 'string'
        ? get(filterTagsIcon, tag)
        : '🍽️',
  }));

  const selectedCategory = selectedFilterTags.length ? selectedFilterTags[0] : 'all';

  const onSelectCategory = catId => {
    if (catId === 'all') {
      handleCancel();
      return;
    }
    handleSelect([catId]);
  };

  const onSelectRestaurant = restaurant => {
    if (restaurant.availableForOrdering === false) return;
    setLoadingRestaurant(restaurant.id);
    setTimeout(() => {
      history.push(`/chefs/${restaurant.slug}`);
    }, 400);
  };

  if (loading) {
    return (
      <main className="search-restaurant pb-6 relative z-0">
        <DeliveryInfoFormInline
          isAbleToChangeReceivingMethod
          onSubmit={onDeliveryFormSubmit}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
          <div className="h-7 w-64 bg-gray-100 rounded-lg animate-pulse mb-6" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden border border-gray-200/80"
              >
                <div className="h-36 bg-gray-100 animate-pulse relative flex items-center justify-center">
                  <img
                    src="/panda_logo.jpg"
                    alt=""
                    className="w-10 h-10 rounded-full opacity-20 object-cover"
                  />
                </div>
                <div className="p-3.5 space-y-2.5">
                  <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse" />
                  <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="search-restaurant pb-6 relative z-0">
      <DeliveryInfoFormInline
        isAbleToChangeReceivingMethod
        onSubmit={onDeliveryFormSubmit}
      />
      <BrowseView
        restaurants={mappedRestaurants}
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={onSelectCategory}
        onSelectRestaurant={onSelectRestaurant}
        searchQuery={searchQuery}
        isExpressMode={false}
        loadingRestaurantId={loadingRestaurant}
      />
      {totalPage > currentPage && (
        <div className="container-fluid pb-5 text-center">
          <button
            type="button"
            className="btn btn-outline-primary px-4"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </main>
  );
};

export default PandaBrowse;
